import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Report from "@/services/analytics/report";

export interface ContactMovementsParams {
  organizationId: string;
  bucketOptionId: string;
  period?: string | null;
  startDate?: string;
  endDate?: string;
  startDate2?: string;
  endDate2?: string;
  createdById?: string;
}

export function useContactMovements(
  params: Ref<ContactMovementsParams | null> | ContactMovementsParams | null
) {
  const queryKey = computed(() => {
    const p = unref(params);
    if (!p) return ["contactMovements", "disabled"];

    return [
      "contactMovements",
      p.organizationId,
      p.bucketOptionId,
      p.period || "",
      p.startDate || "",
      p.endDate || "",
      p.startDate2 || "",
      p.endDate2 || "",
      p.createdById || "",
    ];
  });

  const enabled = computed(() => {
    const p = unref(params);
    return p !== null && p !== undefined && !!p.organizationId && !!p.bucketOptionId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = unref(params);
      if (!p) throw new Error("Parameters not ready");

      const queryParams: Record<string, any> = {
        organizationId: p.organizationId,
        bucketOptionId: p.bucketOptionId,
      };

      if (p.period) {
        queryParams.period = p.period;
      }
      if (p.startDate) queryParams.startDate = p.startDate;
      if (p.endDate) queryParams.endDate = p.endDate;
      if (p.startDate2) queryParams.startDate2 = p.startDate2;
      if (p.endDate2) queryParams.endDate2 = p.endDate2;
      if (p.createdById) queryParams.createdById = p.createdById;

      const result = await Report.getContactMovements(queryParams);

      if (result.data?.status) {
        return result.data.data;
      }

      throw new Error(result.data?.msg || "Failed to load contact movements");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
